import { Injectable } from "@angular/core";
import { Progress as ProgressDto } from "src/app/proxy/progresses/models";
import { Progress } from "../../entities/progress";
import { LocalDbService } from "../local-db-service";

@Injectable({ providedIn: 'root' })
export class ProgressRepository {
  private get table() {
    return this.db.progressesTable;
  }

  constructor(private readonly db: LocalDbService) {}

  async getAll(): Promise<Progress[]> {
    const rows = await this.table.toArray();
    return this.mapRows(rows);
  }

  async getByOriginId(originId: string): Promise<Progress | undefined> {
    const row = await this.table.get(originId);
    return row ? new Progress(row) : undefined;
  }

  async getAllByRoadId(roadId: number): Promise<Progress[]> {
    const rows = await this.table
      .where('roadId')
      .equals(roadId)
      .toArray();
    return this.mapRows(
      rows
        .filter(row => row.isRejected !== true)
        .sort((a, b) => {
          const aTimestamp = Date.parse(a.date ?? '');
          const bTimestamp = Date.parse(b.date ?? '');
          const aIsValid = Number.isFinite(aTimestamp);
          const bIsValid = Number.isFinite(bTimestamp);

          if (aIsValid && bIsValid) {
            return bTimestamp - aTimestamp;
          }

          if (aIsValid) {
            return -1;
          }

          return bIsValid ? 1 : 0;
        }),
    );
  }

  async getPendingSendProgresses(): Promise<Progress[]> {
    const rows = await this.table.filter(progress => progress.pendingSend === true).toArray();
    return rows.map(row => new Progress(row));
  }

  async create(progress: Progress | ProgressDto): Promise<string> {
    const row = new Progress(progress);
    await this.table.add(row);
    return row.originId;
  }

  async upsertBulk(progresses: Array<Progress | ProgressDto>): Promise<void> {
    if (progresses.length === 0) {
      return;
    }

    const rows = progresses.map(progress => new Progress(progress));
    await this.table.bulkPut(rows);
  }

  async rejectByOriginId(originId: string): Promise<void> {
    await this.table.update(originId, { isRejected: true });
  }

  async setPendingSendByOriginId(originId: string, pendingSend: boolean): Promise<void> {
    await this.table.update(originId, { pendingSend });
  }

  async deleteByOriginId(originId: string): Promise<void> {
    await this.table.delete(originId);
  }

  async clear(): Promise<void> {
    await this.table.clear();
  }

  private mapRows(rows: Progress[]): Progress[] {
    return rows;
  }

  async getPendingSendCount(): Promise<number> {
    return this.table.filter(progress => progress.pendingSend === true).count();
  }
}
